import type { LanguageData } from "./types";

export const languageData: LanguageData = {
  slug: "docker",
  name: "Docker / CI-CD",
  nameEn: "Docker & CI/CD",
  seo: {
    title:
      "Docker / CI-CDを学べる女性向けプログラミングスクール | ゆめさくエンジニア",
    description:
      "DockerとCI/CDはモダンな開発現場に不可欠な技術です。ゆめさくエンジニアならコンテナ化からGitHub Actionsによる自動デプロイまで実践的に学べます。",
    url: "/columns/docker-programming-school",
  },
  hero: {
    title: "Docker / CI-CDを学べる女性向けプログラミングスクール",
    englishTitle: "Learn Docker & CI/CD",
  },
  breadcrumb: [
    { name: "ホーム", url: "/" },
    { name: "プログラミング言語・技術別", url: "/columns/category/lang" },
    { name: "Docker / CI-CD" },
  ],
  overview: {
    heading: "Docker / CI-CDとは？",
    paragraphs: [
      "Dockerは、アプリケーションとその実行環境をまとめて「コンテナ」にパッケージングする技術です。「自分のパソコンでは動くのにサーバーでは動かない」という問題を解消し、開発環境と本番環境の差異をなくします。",
      "CI/CD（Continuous Integration / Continuous Delivery）は、コードの変更を自動でテスト・ビルド・デプロイするパイプラインの仕組みです。GitHub ActionsやCircleCIなどのツールを使い、手動作業によるミスを減らし、リリースのスピードを向上させます。",
      "DockerとCI/CDを組み合わせることで、コードをプッシュすれば自動でテストが実行され、問題がなければそのまま本番環境にデプロイされる、モダンな開発フローを実現できます。",
    ],
    useCases: [
      {
        label: "開発環境の統一",
        description:
          "Docker Composeでデータベース・キャッシュ・アプリサーバーをまとめて起動。チーム全員が同じ環境で開発できます。",
      },
      {
        label: "自動テスト",
        description:
          "プルリクエストを作成すると自動でユニットテスト・結合テストが実行され、品質を担保した状態でコードレビューに進めます。",
      },
      {
        label: "自動デプロイ",
        description:
          "mainブランチへのマージをトリガーに、ビルド・デプロイが自動実行されます。手動デプロイによるヒューマンエラーを完全に排除します。",
      },
      {
        label: "マイクロサービスの運用",
        description:
          "各サービスをコンテナ化し、独立してスケーリング・デプロイ可能なアーキテクチャを実現します。",
      },
    ],
  },
  whyLearn: {
    heading: "Docker / CI-CDを学ぶべき3つの理由",
    items: [
      {
        title: "モダンな開発現場では必須のスキル",
        description:
          "スタートアップから大企業まで、Docker・CI/CDを導入していない開発チームはほぼありません。求人票にも「Docker経験」「CI/CD構築経験」が頻繁に記載されており、転職市場で大きなアドバンテージになります。",
      },
      {
        title: "チーム開発の生産性を劇的に向上",
        description:
          "「環境構築に1日かかった」「デプロイで障害が発生した」といった問題をDocker・CI/CDが解決します。これらのスキルを持つエンジニアはチーム全体の生産性を底上げでき、非常に重宝されます。",
      },
      {
        title: "DevOps・SREへのキャリアパス",
        description:
          "Docker・CI/CDの知識は、高年収が期待できるDevOpsエンジニアやSREへのキャリアの入り口です。インフラとアプリケーションの両方を理解する希少な人材として評価されます。",
      },
    ],
  },
  luagateCurriculum: {
    heading: "ゆめさくエンジニアでDocker / CI-CDを学ぶ",
    paragraphs: [
      "ゆめさくエンジニアではPhase 5の応用としてDockerとCI/CDを学びます。Phase 1〜4で開発したWebアプリケーションをDockerでコンテナ化し、GitHub Actionsで自動テスト・デプロイするパイプラインを構築するところまで一貫して体験します。",
      "単にツールの使い方を覚えるだけでなく、なぜコンテナ化するのか、なぜCI/CDが必要なのかという設計思想から理解するため、現場で応用の効く知識が身につきます。",
    ],
    topics: [
      "Dockerの基本概念・Dockerfile作成",
      "Docker Composeによる複数コンテナの管理",
      "コンテナのネットワーク・ボリューム設計",
      "GitHub Actionsによる自動テスト設定",
      "自動ビルド・自動デプロイパイプライン構築",
      "コンテナイメージの最適化・セキュリティ対策",
    ],
  },
  careerPaths: {
    heading: "Docker / CI-CDを活かせるキャリア",
    items: [
      {
        title: "DevOpsエンジニア",
        description:
          "開発と運用のプロセスを統合・自動化するスペシャリストです。CI/CDパイプラインの設計・構築、インフラのコード化（IaC）、モニタリング体制の整備を通じて、組織全体の開発効率を高めます。",
        salary: "550〜950万円",
      },
      {
        title: "SRE（Site Reliability Engineer）",
        description:
          "サービスの信頼性を高めるためにソフトウェアエンジニアリングの手法を適用します。コンテナオーケストレーション、障害の自動復旧、カオスエンジニアリングなど高度な技術を駆使します。",
        salary: "600〜1,000万円",
      },
      {
        title: "バックエンドエンジニア",
        description:
          "Docker・CI/CDの知識を持つバックエンドエンジニアは、開発だけでなくデプロイ・運用まで一貫して担当でき、チームへの貢献度が高いです。フルスタックに近い動きができます。",
        salary: "500〜800万円",
      },
    ],
  },
  comparison: {
    heading: "独学 vs ゆめさくエンジニア",
    items: [
      {
        label: "学習期間",
        selfStudy: "4〜8ヶ月（概念理解に時間がかかる）",
        luagate: "2〜3ヶ月（実際のプロジェクトで即実践）",
      },
      {
        label: "挫折率",
        selfStudy: "環境構築のエラーで挫折する人が多い",
        luagate: "メンターがトラブルシューティングをサポート",
      },
      {
        label: "実践プロジェクト",
        selfStudy: "Hello Worldレベルのコンテナ作成で止まる",
        luagate: "自作アプリのコンテナ化〜自動デプロイまで完遂",
      },
      {
        label: "質問対応",
        selfStudy: "エラーログの読み方がわからず詰まる",
        luagate: "現役DevOpsエンジニアに直接質問可能",
      },
      {
        label: "就職サポート",
        selfStudy: "すべて自力で進める",
        luagate: "ポートフォリオ添削・面接対策まで一貫サポート",
      },
    ],
  },
  faq: [
    {
      question: "Dockerを学ぶのにLinuxの知識は必要ですか？",
      answer:
        "DockerはLinuxのコンテナ技術をベースにしているため、基本的なLinuxコマンドの知識があると理解が深まります。ゆめさくエンジニアではPhase 5でLinux → Dockerの順に学ぶため、必要な知識を段階的に積み上げられます。",
    },
    {
      question: "CI/CDとは具体的にどんなことをするのですか？",
      answer:
        "CI（継続的インテグレーション）はコードの変更のたびに自動でテストを実行し、バグを早期発見します。CD（継続的デリバリー）はテストをパスしたコードを自動で本番環境にデプロイします。手作業のミスを減らし、リリース頻度を高める仕組みです。",
    },
    {
      question: "DockerとKubernetesの違いは何ですか？",
      answer:
        "Dockerは一つのコンテナを作成・実行する技術で、Kubernetesは大量のコンテナを管理・運用するためのオーケストレーションツールです。まずDockerでコンテナの基礎を理解し、その後Kubernetesに進むのがスムーズです。",
    },
    {
      question: "Docker / CI-CDは実務経験がないと評価されませんか？",
      answer:
        "個人プロジェクトでもDocker化やCI/CDパイプライン構築の経験があれば十分に評価されます。ゆめさくエンジニアでは実務レベルのCI/CDパイプラインを構築するため、ポートフォリオとして転職活動に活用できます。",
    },
  ],
  relatedLanguages: [
    { slug: "linux", name: "Linux" },
    { slug: "aws", name: "AWS / クラウド" },
    { slug: "kubernetes", name: "Kubernetes" },
    { slug: "terraform", name: "Terraform" },
  ],
};
